// Accountant's Commission Approvals queue: every approved sale turn-in from the
// current year, with the split and dollar breakdown, approved or flagged one by
// one so accounting always knows what commission checks are coming. Team Leads
// (Admin) get a read-only view of the same tabs. All queue/tab/recipient rules
// live in utils/accountingReview.js — do not re-derive them here.
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckCircle2, ChevronRight, Flag } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { useActionDialog } from "../ActionDialog";
import {
  getAlldealforAdmin,
  getAllusers,
  updateItem,
} from "../../utils/firebasefunctions";
import {
  formatCurrency,
  formatDates,
  notifyCommissionFlagged,
} from "../../utils/helper";
import {
  ACCOUNTING_STATUS,
  accountingTab,
  buildAccountingApprovePatch,
  buildAccountingFixPatch,
} from "../../utils/accountingReview";
import { isDemoUser } from "../../utils/roles";
import FixDealDialog from "./FixDealDialog";

const TABS = [
  { key: "needs-review", label: "Needs Review" },
  { key: "approved", label: "Approved" },
  { key: "flagged", label: "Fix Requested" },
];

const fmtWhen = (ts) => {
  const n = Number(ts || 0);
  if (!n) return "";
  const d = new Date(n);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate(),
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

const splitLabel = (deal) => {
  if (deal?.fixedagentcommission) {
    return `flat ${formatCurrency(deal.agentCommission)}`;
  }
  const pct = Number(deal?.agentCommission);
  return pct ? `agent ${pct}%` : "";
};

function MoneyCell({ label, value, note }) {
  return (
    <div>
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className="text-[15px] font-semibold text-slate-900">
        {formatCurrency(value)}
      </div>
      {note ? <div className="text-[11px] text-gray-400">{note}</div> : null}
    </div>
  );
}

export default function MainCommissionApprovals() {
  const navigate = useNavigate();
  const [{ user, users }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const isAccountant = user?.role === "Accountant";
  const isAdmin = user?.role === "Admin";
  // The demo account can "view as" Accountant — it gets the same read-only
  // treatment as Admin, never live approve/flag writes.
  // Accountants AND Team Leads can approve or ask for a fix — Henry owns the
  // business and shouldn't be a spectator on his own commissions
  // (2026-08-01). Demo/preview accounts stay read-only.
  const canAct = (isAccountant || isAdmin) && !isDemoUser(user);

  const [deals, setDeals] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("needs-review");
  const [busyId, setBusyId] = useState("");
  const [fixTarget, setFixTarget] = useState(null);

  useEffect(() => {
    if (!isAccountant && !isAdmin) return undefined;
    let alive = true;
    (async () => {
      const all = await getAlldealforAdmin("deals", user?.id);
      if (!alive) return;
      setDeals(Array.isArray(all) ? all : []);
      setLoaded(true);
    })();
    return () => {
      alive = false;
    };
  }, [isAccountant, isAdmin, user?.id]);

  const byTab = useMemo(() => {
    const groups = { "needs-review": [], approved: [], flagged: [] };
    deals.forEach((d) => {
      const t = accountingTab(d);
      if (t) groups[t].push(d);
    });
    groups["needs-review"].sort(
      (a, b) => Number(b.createdat || 0) - Number(a.createdat || 0),
    );
    groups.approved.sort(
      (a, b) =>
        Number(b.accountingReviewedAt || 0) -
        Number(a.accountingReviewedAt || 0),
    );
    groups.flagged.sort(
      (a, b) =>
        Number(b.accountingFlaggedAt || 0) - Number(a.accountingFlaggedAt || 0),
    );
    return groups;
  }, [deals]);

  if (!isAccountant && !isAdmin) return <Navigate to="/dashboard" replace />;

  const patchLocal = (id, patch) =>
    setDeals((prev) =>
      prev.map((d) =>
        String(d.createdat) === String(id) ? { ...d, ...patch } : d,
      ),
    );

  const approveDeal = async (deal) => {
    if (busyId) return;
    const id = String(deal.createdat);
    setBusyId(id);
    const patch = buildAccountingApprovePatch(user?.id, Date.now());
    const res = await updateItem("deals", id, patch);
    setBusyId("");
    if (res === "success") {
      patchLocal(id, patch);
    } else {
      toast.error("Couldn't save the approval. Try again.");
    }
  };

  const submitFix = async (note) => {
    if (!fixTarget || !note || busyId) return;
    const id = String(fixTarget.createdat);
    setBusyId(id);
    const patch = buildAccountingFixPatch(user?.id, note, Date.now());
    const res = await updateItem("deals", id, patch);
    setBusyId("");
    if (res === "success") {
      patchLocal(id, patch);
      const target = fixTarget;
      // The context roster can be empty right after login — fetch a fresh
      // list rather than silently alerting no one about the fix request.
      (async () => {
        let roster = users;
        if (!roster?.length) {
          roster = (await getAllusers("users").catch(() => [])) || [];
        }
        if (!roster.length) {
          toast.warn(
            "Fix request saved, but no one could be alerted. Let the approver know directly.",
          );
          return;
        }
        await notifyCommissionFlagged(target, roster, user, note);
      })().catch((e) => console.error("Fix notification failed:", e));
      setFixTarget(null);
      setTab("flagged");
    } else {
      toast.error("Couldn't send the fix request. Try again.");
    }
  };

  const markAllReviewed = async () => {
    const queue = byTab["needs-review"];
    if (!queue.length || busyId) return;
    const ok = await confirm({
      title: "Mark All Reviewed",
      body: `Mark all ${queue.length} deals as reviewed?`,
      note: "Each will move to the Approved tab.",
      confirmLabel: "Mark All Reviewed",
    });
    if (!ok) return;
    setBusyId("all");
    const stamp = Date.now();
    let failures = 0;
    for (const d of queue) {
      const id = String(d.createdat);
      const patch = {
        accountingStatus: ACCOUNTING_STATUS.APPROVED,
        accountingReviewedBy: user?.id || "",
        accountingReviewedAt: stamp,
      };
      // eslint-disable-next-line no-await-in-loop
      const res = await updateItem("deals", id, patch);
      if (res === "success") patchLocal(id, patch);
      else failures += 1;
    }
    setBusyId("");
    if (failures)
      toast.error(`${failures} deals didn't save. They stayed in Needs Review.`);
  };

  const rows = byTab[tab];

  return (
    <div className="w-[96%] mx-auto my-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold text-slate-900">
          Commission Approvals
        </h1>
        {!canAct && (
          <span className="text-[12px] text-gray-500 bg-gray-100 rounded-full px-3 py-1">
            View only
          </span>
        )}
      </div>
      <p className="text-[13px] text-gray-500 mb-5">
        Approved deals waiting for accounting review
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-[13px] rounded-full px-4 py-1.5 border transition ${
              tab === t.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
            }`}
          >
            {t.label}
            <span
              className={`ml-2 text-[11px] font-bold rounded-full px-1.5 py-0.5 ${
                tab === t.key ? "bg-white/20" : "bg-gray-100"
              }`}
            >
              {byTab[t.key].length}
            </span>
          </button>
        ))}
        <span className="flex-1" />
        {canAct && tab === "needs-review" && rows.length > 0 && (
          <button
            onClick={markAllReviewed}
            disabled={!!busyId}
            className="text-[13px] rounded-full px-4 py-1.5 border border-gray-200 bg-white text-gray-600 hover:border-gray-400 disabled:opacity-50"
          >
            {busyId === "all" ? "Saving…" : "Mark All Reviewed"}
          </button>
        )}
      </div>

      {!loaded ? (
        <div className="text-center text-gray-400 py-16 text-[14px]">
          Loading deals…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 py-16 text-center text-gray-400 text-[14px]">
          {tab === "needs-review"
            ? "All caught up. New deals appear here as soon as they're approved."
            : tab === "approved"
              ? "Nothing approved yet."
              : "No fix requests."}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((deal) => {
            const id = String(deal.createdat);
            const flagged =
              deal.accountingStatus === ACCOUNTING_STATUS.FLAGGED;
            return (
              <div
                key={id}
                onClick={() => {
                  // Don't navigate away when she's selecting a figure to copy.
                  if (window.getSelection?.()?.toString()) return;
                  navigate(`/dashboard/deals-detail/${id}`);
                }}
                className={`bg-white rounded-2xl border p-4 sm:p-5 cursor-pointer transition-colors hover:border-gray-400 ${
                  flagged ? "border-amber-300 hover:border-amber-400" : "border-gray-200"
                }`}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-[200px]">
                    <Link
                      to={`/dashboard/deals-detail/${id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[15px] font-semibold text-slate-900 hover:underline"
                    >
                      {deal.propertyaddress || "No address"}
                    </Link>
                    <div className="text-[12px] text-gray-500">
                      {deal.dealtype || "Deal"} · {deal.dealStage || "-"}
                      {deal.closingdate
                        ? ` · closing ${formatDates(deal.closingdate)}`
                        : ""}
                    </div>
                  </div>
                  <span className="flex-1" />
                  <div className="text-right">
                    <div className="text-[13px] font-semibold text-slate-900">
                      {deal.agentName || "No agent"}
                    </div>
                    <div className="text-[12px] text-gray-500">
                      {[
                        deal.splitdescription,
                        deal.captypedescription,
                        splitLabel(deal),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <div className="text-[12px] text-gray-400 inline-flex items-center gap-0.5 self-center">
                    View deal <ChevronRight size={14} />
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 border-t border-gray-100 mt-3 pt-3">
                  <MoneyCell
                    label="Total commission"
                    value={deal.totalCommissionamount}
                  />
                  <MoneyCell
                    label="Agent gets"
                    value={deal.agentCommissionamount}
                    note={
                      Number(deal.agentdiscount || 0) > 0
                        ? `after ${formatCurrency(deal.agentdiscount)} discount`
                        : ""
                    }
                  />
                  <MoneyCell
                    label="Team lead gets"
                    value={deal.teamleadCommissionamount}
                    note={
                      Number(deal.teamleaddiscount || 0) > 0
                        ? `after ${formatCurrency(deal.teamleaddiscount)} discount`
                        : ""
                    }
                  />
                  <MoneyCell label="CBR (broker)" value={deal.bankcommission} />
                  <MoneyCell
                    label="Broker admin fee"
                    value={deal.brokeradminfee}
                  />
                  {Number(deal.thirdpartyagentcommissionamount || 0) > 0 && (
                    <MoneyCell
                      label="Referral agent"
                      value={deal.thirdpartyagentcommissionamount}
                    />
                  )}
                  {Number(deal.leadsourceCommissionamount || 0) > 0 && (
                    <MoneyCell
                      label="Lead source"
                      value={deal.leadsourceCommissionamount}
                    />
                  )}
                </div>

                {flagged && (
                  <div className="mt-3 text-[13px] text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                    "{deal.accountingFlagNote}" · fix requested{" "}
                    {fmtWhen(deal.accountingFlaggedAt)}
                  </div>
                )}

                {tab === "approved" && (
                  <div className="mt-3 text-[12px] text-emerald-700">
                    Approved for accounting {fmtWhen(deal.accountingReviewedAt)}
                  </div>
                )}

                {canAct && tab !== "approved" && (
                  <div
                    className="flex justify-end gap-2 mt-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!flagged && (
                      <button
                        onClick={() => setFixTarget(deal)}
                        disabled={!!busyId}
                        className="inline-flex items-center gap-1.5 text-[13px] rounded-full px-4 py-1.5 border border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-700 disabled:opacity-50"
                      >
                        <Flag size={14} /> Fix
                      </button>
                    )}
                    <button
                      onClick={() => approveDeal(deal)}
                      disabled={!!busyId}
                      className="inline-flex items-center gap-1.5 text-[13px] rounded-full px-4 py-1.5 bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      <CheckCircle2 size={14} />
                      {busyId === id || busyId === "all" ? "Saving…" : "Approve"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {fixTarget && (
        <FixDealDialog
          deal={fixTarget}
          busy={!!busyId}
          onCancel={() => setFixTarget(null)}
          onSubmit={submitFix}
        />
      )}
      {dialog}
    </div>
  );
}
